import React, { useEffect } from 'react';

import { Link, useParams } from 'react-router-dom';

import { bemCssModules, getIntlDate, getTestId } from '@utils';
import { useRedirectToDefaultCurrency, useLocation, useNavigate } from '@utils/hooks';
import { LocationStateType } from '@utils/types';

import { useStore, walletActions } from '@modules';
import { CryptoCurrencyEnum } from '@modules/global/types';
import { OperationsEnum, TLimits } from '@modules/wallet/types';

import CryptoMenu from '@components/CryptoMenu';
import LoaderLayout from '@layouts/LoaderLayout';
import MainLayout from '@layouts/MainLayout';

import LimitBlock from './LimitBlock';
import CloseIcon from './icons/close.svg';

import style from './Limits.scss';

const cn = bemCssModules(style, 'Limits');
const ti = getTestId('Limits');

export const Limits: CustomFC = ({ className }) => {
  const location = useLocation<LocationStateType['limits']>();
  const { state } = location;
  const navigate = useNavigate();

  const { currency } = useParams() as { currency: CryptoCurrencyEnum };
  const [{ wallet, user, global }, dispatch] = useStore();

  const from = state?.from;
  const defaultCurrency = useRedirectToDefaultCurrency();
  const cryptoCurrency = from ? currency : defaultCurrency;

  const date = new Date();
  const prevWeek = date.getDate() - 7;
  const prevMonth = date.getMonth() - 1;
  const weekAgo = new Date(date.getFullYear(), date.getMonth(), prevWeek);
  const monthAgo = new Date(date.getFullYear(), prevMonth, date.getDate());
  const language = user.data?.language_code;

  const fiatCurrency = state?.fiatCurrency || user.data?.fiat_currency;

  useEffect(() => {
    const data = { crypto_currency: cryptoCurrency, currency: fiatCurrency };
    dispatch(walletActions.getWalletLimits(data));
  }, [location.pathname]);

  const isLoading = wallet.loaders.getWalletLimits;
  const limits = Object.entries(wallet.limits).reduce((acc, [operation, item]) => {
    if (!['buy', 'sell', 'withdraw'].includes(operation)) return acc;
    const day =
      item[currency]?.day && item[currency]?.init_day
        ? {
            value: item[currency]?.day,
            init_value: item[currency]?.init_day,
            fiat_value: item[fiatCurrency]?.day,
            fiat_init_value: item[fiatCurrency]?.init_day,
            title: getIntlDate(date, { month: 'short', day: '2-digit' }, language),
          }
        : {};
    const week =
      item[currency]?.week && item[currency]?.init_week
        ? {
            value: item[currency]?.week,
            init_value: item[currency]?.init_week,
            fiat_value: item[fiatCurrency]?.week,
            fiat_init_value: item[fiatCurrency]?.init_week,
            title: `
              ${getIntlDate(weekAgo, { month: 'short', day: '2-digit' }, language)} – 
              ${getIntlDate(date, { month: 'short', day: '2-digit' }, language)}
            `,
          }
        : {};
    const month =
      item[currency]?.month && item[currency]?.init_month
        ? {
            value: item[currency]?.month,
            init_value: item[currency]?.init_month,
            fiat_value: item[fiatCurrency]?.month,
            fiat_init_value: item[fiatCurrency]?.init_month,
            title: `
              ${getIntlDate(monthAgo, { month: 'short', day: '2-digit' }, language)} – 
              ${getIntlDate(date, { month: 'short', day: '2-digit' }, language)}
            `,
          }
        : {};

    acc[operation] = { day, week, month };

    return acc;
  }, {} as TLimits);

  const onBack = () => navigate(CONST.nav.wallets);

  return (
    <MainLayout className={cn(null, [className])} data-testid={ti()} backButton={onBack}>
      <div className={cn('content')}>
        <div className={cn('currencies')}>
          <CryptoMenu activeTab={currency} tabs={global.currencies.crypto} />
        </div>
        {from && (
          <Link to={from} className={cn('close-button')}>
            <CloseIcon />
          </Link>
        )}
        <LoaderLayout isLoading={isLoading}>
          {Object.entries(limits).map(([operation, item]) => {
            const min = wallet.limits[operation][currency]?.min;
            const max = wallet.limits[operation][currency]?.max;
            const { day, week, month } = limits[operation];
            const hasPeriods =
              !!Object.values(day).length &&
              !!Object.values(week).length &&
              !!Object.values(month).length;
            if ((!min || min === '0') && (!max || max === '0') && !hasPeriods) return null;
            return (
              <LimitBlock
                key={operation}
                currency={cryptoCurrency}
                fiatCurrency={fiatCurrency}
                limits={item}
                operation={operation as OperationsEnum}
                min={min}
                max={max}
              />
            );
          })}
        </LoaderLayout>
      </div>
    </MainLayout>
  );
};

export default Limits;
